import { defineStore } from 'pinia';
import { AxiosResponse } from 'axios';
import { computed, ref, Ref } from 'vue';
import { addDays, endOfMonth, endOfYear, format, startOfMonth, startOfYear } from 'date-fns';

import apiGw from '@/services/apiGw';
import { useUserStore } from '@/store/userStore';
import { getUidFromSelfLink } from '@/utils/links';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { SpendingsTimeframe } from '@/components/transactions/types/spendings';
import { TagsProfile, TrxReceipt } from '@/components/transactions/types/receiptFile';
import { Transaction, TransactionEndPoints, TrxFiltersType } from '@/components/transactions/types/transactions';

import { SseListener } from '@/utils/general';
import { useEventStreamStore } from '@/store/eventStreamStore';
import { usePartnershipsStore } from '@/store/partnershipsStore';

enum SpendingType {
  SpendingsDaily = 'spendingsDaily',
  SpendingsMonthly = 'spendingsMonthly'
}
enum PendingPromiseType {
  PendingDailyPromise = 'pendingDailyPromise',
  PendingMonthlyPromise = 'pendingMonthlyPromise'
}

export const useTransactionsStore = defineStore('transactions', () => {
  const eventStreamStore = useEventStreamStore();

  // STATES
  const filters: Ref<TrxFiltersType> = ref({});
  const transactions: Ref<Transaction[]> = ref([]);
  const transactionsDetailsLoaded: Ref<Set<string>> = ref(new Set());
  const transactionUids: Ref<Set<string>> = ref(new Set());
  const tagsProfile: Ref<TagsProfile> = ref({ tags: [], mentions: [] });
  const spendingsDaily: Ref<{ [key: string]: SpendingsTimeframe }> = ref({});
  const spendingsMonthly: Ref<{ [key: string]: SpendingsTimeframe }> = ref({});
  const pendingDailyPromise: Ref<{ [key: string]: Promise<AxiosResponse> }> = ref({});
  const pendingMonthlyPromise: Ref<{ [key: string]: Promise<AxiosResponse> }> = ref({});
  const transactionReceipts: Ref<{ [key: string]: TrxReceipt }> = ref({});
  const pendingReceiptPromise: Ref<{ [key: string]: Promise<AxiosResponse> }> = ref({});

  const refs = {
    spendingsDaily,
    spendingsMonthly,
    pendingDailyPromise,
    pendingMonthlyPromise
  };

  // GETTERS
  const hasAnyFilters = computed(() => {
    // Returns true if and only if there is at least one filter value set to a non-empty string or not undefined
    return Object.values(filters.value).some((value) => !!value);
  });

  // ACTIONS

  function $reset() {
    filters.value = {};
    transactions.value = [];
    transactionsDetailsLoaded.value = new Set();
    transactionUids.value = new Set();
    tagsProfile.value = { tags: [], mentions: [] };
    spendingsDaily.value = {};
    spendingsMonthly.value = {};
    pendingDailyPromise.value = {};
    pendingMonthlyPromise.value = {};
    transactionReceipts.value = {};
    pendingReceiptPromise.value = {};
  }

  /**
   * Retrieve the spendings for a given month
   * @param month the month to retrieve the spendings for
   * @param batch if true, the request will be made for the whole year
   * @returns
   */
  async function getSpendingsForMonth(month: string, batch?: boolean) {
    return await _spendingsFor(new Date(`${month}`), SpendingType.SpendingsMonthly, batch);
  }
  async function _spendingsFor(date: Date, type: SpendingType, batch?: boolean) {
    const userStore = useUserStore();
    const contextSwitchStore = useContextSwitchStore();
    const npUid = userStore.uid;
    /**
     * Currently stats for partnerships are disabled => only return in case of private account
     */
    if (!contextSwitchStore.isPrivateContext) {
      return undefined;
    }

    /**
     * In order to save on the number of request, we automatically ask for all days since the 1st of
     * the month in case of daily stat and from the start of the year for monthly stat.
     */
    let fromDate;
    if (batch) {
      fromDate = type === SpendingType.SpendingsDaily ? startOfMonth(date) : startOfYear(date);
    } else {
      fromDate = date;
    }
    const fromDateKey =
      type === SpendingType.SpendingsDaily ? format(fromDate, 'yyyy-MM-dd') : format(fromDate, 'yyyy-MM');

    if (!!!refs[type].value[fromDateKey]) {
      const pending =
        type === SpendingType.SpendingsDaily
          ? PendingPromiseType.PendingDailyPromise
          : PendingPromiseType.PendingMonthlyPromise;
      if (!!refs[pending].value[fromDateKey]) {
      }
      if (!!refs[pending].value[fromDateKey]) {
        const promise = refs[pending].value[fromDateKey];
        await promise;
      } else {
        const toDate = type === SpendingType.SpendingsDaily ? endOfMonth(date) : endOfYear(date);
        const request = apiGw.post(TransactionEndPoints.SpendingStats, {
          from: format(fromDate, 'yyyy-MM-dd'),
          to: format(toDate, 'yyyy-MM-dd'),
          pattern: type === SpendingType.SpendingsDaily ? 'YYYY-MM-DD' : 'YYYY-MM',
          filter: { naturalPerson: npUid }
        });
        refs[pending].value[fromDateKey] = request;

        const periods: Array<SpendingsTimeframe> = (await request).data;
        if (periods) {
          periods.forEach((stat) => {
            refs[type].value[stat.period] = stat;
          });
        }
        /**
         * Once the response has been process we can delete the pending promise as we should not need it anymore
         */
        delete refs[pending].value[fromDateKey];
      }
    }

    const formatKey = type === SpendingType.SpendingsDaily ? 'yyyy-MM-dd' : 'yyyy-MM';
    return refs[type].value[format(date, formatKey)];
  }
  async function loadDailySpendingsForRange(fromDate: string, toDate: string) {
    const userStore = useUserStore();
    const contextSwitchStore = useContextSwitchStore();
    const npUid = userStore.uid;
    /**
     * Currently stats for partnerships are disabled => only return in case of private account
     */
    if (!contextSwitchStore.isPrivateContext) {
      return undefined;
    }

    const request = apiGw.post(TransactionEndPoints.SpendingStats, {
      from: format(new Date(fromDate), 'yyyy-MM-dd'),
      to: format(addDays(new Date(toDate), 1), 'yyyy-MM-dd'),
      pattern: 'YYYY-MM-DD',
      filter: { naturalPerson: npUid }
    });

    const periods: Array<SpendingsTimeframe> = (await request).data;
    if (periods) {
      periods.forEach((stat) => {
        spendingsDaily.value[stat.period] = stat;
      });
    }
  }
  async function getTagsProfile() {
    const userStore = useUserStore();
    if (usePartnershipsStore().isRequestorBootstrapperForCurrentPartnership) return;
    let response;
    if (!useContextSwitchStore().isPrivateContext) {
      const partnershipUid = useContextSwitchStore().getCurrentContext()?.uid;
      response = await apiGw.get(`/partnerships/${partnershipUid}/tag-profile`);
    } else {
      const npUid = userStore.uid;
      response = await apiGw.get(`/natural-persons/${npUid}/tag-profile`);
    }
    const tagProfile = response.data.data;
    tagsProfile.value = tagProfile;
  }
  async function getTransactionDetails(transaction: Transaction) {
    const trx = transactions.value.filter((trx) => trx._links.self === transaction._links.self)[0];
    if (trx?._actions || transactionsDetailsLoaded.value.has(trx?._links.self.uri)) {
      return trx;
    }

    const response = await apiGw.get(`/magic${transaction._links.self.uri}`);
    const trxDetail: Transaction = response.data;
    updateTransaction(trxDetail);
    transactionsDetailsLoaded.value.add(trxDetail._links.self.uri);
    return trxDetail;
  }
  async function loadTransactionReceipt(transactionUid: string, forceReload = false) {
    if (!forceReload && transactionReceipts.value[transactionUid]) {
      return transactionReceipts.value[transactionUid];
    }

    if (!forceReload && !!pendingReceiptPromise.value[transactionUid]) {
      await pendingReceiptPromise.value[transactionUid];
    } else {
      try {
        const request = apiGw.get(`/v2/transactions/${transactionUid}/receipt`);
        pendingReceiptPromise.value[transactionUid] = request;
        const response = await request;
        transactionReceipts.value[transactionUid] = response.data;
        unstaleTrxReceiptUidIfOlder(transactionUid, new Date().getTime());
        delete pendingReceiptPromise.value[transactionUid];
        /**
         * Now we update the transaction with the attached receipt information so that we can display it in the UI
         */
        const trx = transactions.value.find((trx) => getUidFromSelfLink(trx._links.self) === transactionUid);
        if (!!trx) {
          trx.attachedReceipt = {
            hasAttachment: !!response.data.attachedFiles.length,
            hasNote: !!response.data.noteText
          };
        }
      } catch (error) {
        console.error(`Error getting Receipt: `, error);
      }
    }
    return transactionReceipts.value[transactionUid];
  }
  function setTransactions(trxs: Array<Transaction>) {
    transactions.value = trxs;
    transactionUids.value = new Set(trxs.map((trx) => getUidFromSelfLink(trx._links.self)));
  }
  function appendTransactions(newTransactions: Array<Transaction>) {
    newTransactions.forEach((newTrx) => {
      const trxUid = getUidFromSelfLink(newTrx._links.self);
      if (!transactionUids.value.has(trxUid)) {
        transactions.value.push(newTrx);
        transactionUids.value.add(trxUid);
      }
    });
  }
  function updateTransaction(transaction: Transaction) {
    const trxIndex = transactions.value.findIndex((trx) => trx._links.self.uri === transaction._links.self.uri);
    transactions.value[trxIndex] = { ...transactions.value[trxIndex], ...transaction };
  }
  function resetTransactions() {
    transactions.value = [];
    spendingsMonthly.value = {};
    spendingsDaily.value = {};
    pendingMonthlyPromise.value = {};
    pendingDailyPromise.value = {};
    transactionsDetailsLoaded.value = new Set();
    transactionUids.value = new Set();
  }
  function setFilters(newFilters: TrxFiltersType) {
    filters.value = { ...filters.value, ...newFilters };
  }
  function resetFilters() {
    filters.value = {};
  }

  function unstaleTrxReceiptUidIfOlder(uid: string, timeOfResponse: number) {
    if (!eventStreamStore.stale_trx_receipts) {
      return;
    }
    const staleTime = eventStreamStore.stale_trx_receipts[uid]?.time;
    if (staleTime < timeOfResponse) {
      eventStreamStore.unstaleUid(uid, SseListener.TrxReceipt);
    }
  }

  return {
    // STATES
    filters,
    transactions,
    transactionReceipts,
    pendingReceiptPromise,
    tagsProfile,
    spendingsDaily,
    spendingsMonthly,
    pendingDailyPromise,
    pendingMonthlyPromise,
    // GETTERS
    hasAnyFilters,
    // ACTIONS
    $reset,
    getSpendingsForMonth,
    loadDailySpendingsForRange,
    getTagsProfile,
    getTransactionDetails,
    loadTransactionReceipt,
    setTransactions,
    appendTransactions,
    updateTransaction,
    resetTransactions,
    setFilters,
    resetFilters
  };
});
